import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { X } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';

// Reusable top bar with the language switcher (Amazon-style)
const TopBar = () => (
  <div className="flex items-center justify-between mb-8 pt-2 max-w-6xl mx-auto">
    <div className="w-32" />
    <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif' }}>
      Jobasti<span className="text-blue-600">IQ</span>
    </h1>
    <div className="w-32 flex justify-end">
      <LanguageSwitcher />
    </div>
  </div>
);

// Pre-seeded tech job roles
const TECH_JOB_ROLES = [
  'Frontend Developer',
  'Backend Developer',
  'Full Stack Developer',
  'Software Engineer',
  'Senior Software Engineer',
  'Lead Software Engineer',
  'DevOps Engineer',
  'Data Engineer',
  'Data Scientist',
  'Machine Learning Engineer',
  'AI Engineer',
  'Mobile Developer (iOS)',
  'Mobile Developer (Android)',
  'React Native Developer',
  'UI/UX Designer',
  'Product Designer',
  'Product Manager',
  'Technical Product Manager',
  'Engineering Manager',
  'CTO',
  'VP of Engineering',
  'QA Engineer',
  'Test Automation Engineer',
  'Security Engineer',
  'Cloud Architect',
  'Solutions Architect',
  'Database Administrator',
  'System Administrator',
  'Site Reliability Engineer',
  'Blockchain Developer',
  'Game Developer',
  'Embedded Systems Engineer'
].sort();

// Pre-seeded programming languages and tech skills
const TECH_SKILLS = [
  'JavaScript',
  'TypeScript',
  'Python',
  'Java',
  'C#',
  'C++',
  'C',
  'Go',
  'Rust',
  'Ruby',
  'PHP',
  'Swift',
  'Kotlin',
  'Scala',
  'R',
  'MATLAB',
  'SQL',
  'NoSQL',
  'React',
  'Angular',
  'Vue.js',
  'Node.js',
  'Express.js',
  'Django',
  'Flask',
  'Spring Boot',
  '.NET',
  'ASP.NET',
  'Laravel',
  'Ruby on Rails',
  'Next.js',
  'Nuxt.js',
  'React Native',
  'Flutter',
  'SwiftUI',
  'Xamarin',
  'Docker',
  'Kubernetes',
  'AWS',
  'Azure',
  'Google Cloud',
  'MongoDB',
  'PostgreSQL',
  'MySQL',
  'Redis',
  'Elasticsearch',
  'GraphQL',
  'REST API',
  'Microservices',
  'Git',
  'CI/CD',
  'Jenkins',
  'Terraform',
  'Ansible',
  'Linux',
  'Unix',
  'Bash',
  'TensorFlow',
  'PyTorch',
  'Scikit-learn',
  'Pandas',
  'NumPy',
  'Hadoop',
  'Spark',
  'Kafka',
  'RabbitMQ',
  'Agile',
  'Scrum',
  'Jira',
  'Figma',
  'Sketch',
  'Adobe XD'
].sort();

// Spoken languages
const SPOKEN_LANGUAGES = [
  'English',
  'German',
  'Spanish',
  'French',
  'Italian',
  'Portuguese',
  'Dutch',
  'Russian',
  'Chinese (Mandarin)',
  'Japanese',
  'Korean',
  'Arabic',
  'Hindi',
  'Turkish',
  'Polish',
  'Swedish',
  'Norwegian',
  'Danish',
  'Finnish',
  'Greek'
].sort();

const ProspectRegistration = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [error, setError] = useState('');
  const [invitationEmail, setInvitationEmail] = useState('');

  const [formData, setFormData] = useState({
    // Step 1
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    city: '',
    password: '',
    
    // Step 2
    currentJobTitle: '',
    preferredJobTitles: [],
    salaryExpectations: '',
    
    // Step 3
    skills: [],
    spokenLanguages: [],
    cvFile: null,
    diplomaFile: null,
    bio: '',
    experienceSummary: ''
  });

  const [tempInputs, setTempInputs] = useState({
    preferredJobTitle: '',
    skill: '',
    spokenLanguage: ''
  });

  const [searchInputs, setSearchInputs] = useState({
    jobTitle: '',
    skill: '',
    language: ''
  });

  // Validate invitation token on mount
  React.useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Invalid invitation link. Please contact your administrator.');
        setValidatingToken(false);
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/invite/validate/${token}`);
        const data = await response.json();

        if (data.success) {
          setInvitationEmail(data.data.email);
          setValidatingToken(false);
        } else {
          setError(data.message || 'Invalid invitation link');
          setValidatingToken(false);
        }
      } catch (err) {
        console.error('Error validating invitation:', err);
        setError('Failed to validate invitation. Please try again.');
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [token]);

  // Step 1 handlers
  const handleStep1Next = () => {
    if (!formData.firstName || !formData.lastName || !formData.dateOfBirth || !formData.gender || !formData.city || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }
    setError('');
    setStep(2);
  };

  // Step 2 handlers - Preferred Job Titles
  const addPreferredJobTitle = (jobTitle) => {
    if (jobTitle && !formData.preferredJobTitles.includes(jobTitle)) {
      setFormData({
        ...formData,
        preferredJobTitles: [...formData.preferredJobTitles, jobTitle]
      });
      setSearchInputs({ ...searchInputs, jobTitle: '' });
    }
  };

  const removePreferredJobTitle = (index) => {
    setFormData({
      ...formData,
      preferredJobTitles: formData.preferredJobTitles.filter((_, i) => i !== index)
    });
  };

  const handleStep2Next = () => {
    if (!formData.currentJobTitle || formData.preferredJobTitles.length === 0 || !formData.salaryExpectations) {
      setError('Please fill in all required fields');
      return;
    }
    setError('');
    setStep(3);
  };

  // Step 3 handlers - Skills
  const addSkill = (skill) => {
    if (skill && !formData.skills.includes(skill)) {
      setFormData({
        ...formData,
        skills: [...formData.skills, skill]
      });
      setSearchInputs({ ...searchInputs, skill: '' });
    }
  };

  const removeSkill = (index) => {
    setFormData({
      ...formData,
      skills: formData.skills.filter((_, i) => i !== index)
    });
  };

  // Spoken Languages handlers
  const addSpokenLanguage = (language) => {
    if (language && !formData.spokenLanguages.includes(language)) {
      setFormData({
        ...formData,
        spokenLanguages: [...formData.spokenLanguages, language]
      });
      setSearchInputs({ ...searchInputs, language: '' });
    }
  };

  const removeSpokenLanguage = (index) => {
    setFormData({
      ...formData,
      spokenLanguages: formData.spokenLanguages.filter((_, i) => i !== index)
    });
  };

  // Filter functions for autocomplete
  const getFilteredJobTitles = () => {
    return TECH_JOB_ROLES.filter(role => 
      role.toLowerCase().includes(searchInputs.jobTitle.toLowerCase()) &&
      !formData.preferredJobTitles.includes(role)
    );
  };

  const getFilteredSkills = () => {
    return TECH_SKILLS.filter(skill => 
      skill.toLowerCase().includes(searchInputs.skill.toLowerCase()) &&
      !formData.skills.includes(skill)
    );
  };

  const getFilteredLanguages = () => {
    return SPOKEN_LANGUAGES.filter(lang => 
      lang.toLowerCase().includes(searchInputs.language.toLowerCase()) &&
      !formData.spokenLanguages.includes(lang)
    );
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        [type === 'cv' ? 'cvFile' : 'diplomaFile']: file
      });
    }
  };

  const handleCreateAccount = async () => {
    if (formData.skills.length === 0 || formData.spokenLanguages.length === 0 || !formData.cvFile) {
      setError('Please add skills, spoken languages, and upload your CV');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Step 1: Upload CV first (using a temporary path)
      const tempId = Date.now();
      const cvFileName = `temp-${tempId}-cv.${formData.cvFile.name.split('.').pop()}`;
      const { error: cvUploadError } = await supabase.storage
        .from('resumes')
        .upload(cvFileName, formData.cvFile);

      if (cvUploadError) throw cvUploadError;

      const { data: cvUrlData } = supabase.storage
        .from('resumes')
        .getPublicUrl(cvFileName);

      let diplomaUrl = null;
      if (formData.diplomaFile) {
        const diplomaFileName = `temp-${tempId}-diploma.${formData.diplomaFile.name.split('.').pop()}`;
        const { error: diplomaUploadError } = await supabase.storage
          .from('diplomas')
          .upload(diplomaFileName, formData.diplomaFile);
        
        if (!diplomaUploadError) {
          const { data: diplomaUrlData } = supabase.storage
            .from('diplomas')
            .getPublicUrl(diplomaFileName);
          
          diplomaUrl = diplomaUrlData.publicUrl;
        }
      }

      // Step 2: Call backend API to create account (bypasses Supabase email confirmation)
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/invite/complete-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          email: invitationEmail,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          dateOfBirth: formData.dateOfBirth,
          gender: formData.gender,
          city: formData.city,
          currentJobTitle: formData.currentJobTitle,
          preferredJobTitles: formData.preferredJobTitles,
          salaryExpectations: formData.salaryExpectations,
          skills: formData.skills,
          spokenLanguages: formData.spokenLanguages,
          resumeUrl: cvUrlData.publicUrl,
          diplomaUrl: diplomaUrl,
          bio: formData.bio,
          experienceSummary: formData.experienceSummary
        })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to create account');
      }

      // Success - redirect to login
      alert('Account created successfully! Please login with your credentials.');
      navigate('/login');

    } catch (error) {
      console.error('Registration error:', error);
      setError(error.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while validating token
  if (validatingToken) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <TopBar />
        <div className="flex items-center justify-center pt-12">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Validating invitation...</h2>
            <p className="text-gray-600">Please wait</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error if token validation failed
  if (error && !invitationEmail) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <TopBar />
        <div className="flex items-center justify-center pt-12">
          <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
            <div className="text-red-600 mb-4">
              <X className="w-16 h-16 mx-auto" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Invalid Invitation</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl w-full mx-auto">
        {/* Top bar with language switcher */}
        <div className="flex items-center justify-between mb-8 pt-2">
          <div className="w-32" />
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif' }}>
            Jobasti<span className="text-blue-600">IQ</span>
          </h1>
          <div className="w-32 flex justify-end">
            <LanguageSwitcher />
          </div>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Join Jobasti<span className="text-blue-600">IQ</span></h1>
          <p className="text-gray-600 mt-2">Registration for: <strong>{invitationEmail}</strong></p>
          <p className="text-gray-600 mt-2">Create your prospect profile</p>
        </div>

        <div className="flex gap-6 justify-center">
          {/* Step 1 */}
          <div className={`bg-white rounded-lg shadow-lg p-8 w-80 ${step !== 1 && 'opacity-50'}`}>
            <div className="text-center mb-6">
              <div className="inline-block bg-blue-100 rounded-full px-4 py-1 mb-4">
                <span className="text-blue-600 font-semibold">Step 1</span>
              </div>
              <h2 className="text-xl font-bold text-gray-900">Tell us about yourself:</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name*</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={step !== 1}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name*</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={step !== 1}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth*</label>
                <input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={step !== 1}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender*</label>
                <select
                  value={formData.gender}
                  onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={step !== 1}
                >
                  <option value="">Select gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                  <option value="Prefer not to say">Prefer not to say</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City*</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Berlin"
                  disabled={step !== 1}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password*</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={step !== 1}
                />
              </div>

              {step === 1 && (
                <button
                  onClick={handleStep1Next}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                >
                  Next
                </button>
              )}
            </div>
          </div>

          {/* Step 2 */}
          <div className={`bg-white rounded-lg shadow-lg p-8 w-80 ${step !== 2 && 'opacity-50'}`}>
            <div className="text-center mb-6">
              <div className="inline-block bg-blue-100 rounded-full px-4 py-1 mb-4">
                <span className="text-blue-600 font-semibold">Step 2</span>
              </div>
              <h2 className="text-xl font-bold text-gray-900">What position are you looking for?</h2>
              <p className="text-sm text-gray-600 mt-2">We will select vacancies for you.</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Job Title*</label>
                <select
                  value={formData.currentJobTitle}
                  onChange={(e) => setFormData({ ...formData, currentJobTitle: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={step !== 2}
                >
                  <option value="">Select your current job title</option>
                  {TECH_JOB_ROLES.map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Job Titles*</label>
                <p className="text-xs text-gray-500 mb-2">Select roles you're interested in</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.preferredJobTitles.map((title, index) => (
                    <span key={index} className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">
                      {title}
                      {step === 2 && (
                        <button onClick={() => removePreferredJobTitle(index)} className="hover:text-blue-900">
                          <X size={14} />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
                {step === 2 && (
                  <div className="relative">
                    <input
                      type="text"
                      value={searchInputs.jobTitle}
                      onChange={(e) => setSearchInputs({ ...searchInputs, jobTitle: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Search for job titles..."
                    />
                    {searchInputs.jobTitle && getFilteredJobTitles().length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {getFilteredJobTitles().slice(0, 10).map((role) => (
                          <button
                            key={role}
                            onClick={() => addPreferredJobTitle(role)}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm"
                          >
                            {role}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Salary Expectations, EUR*</label>
                <select
                  value={formData.salaryExpectations}
                  onChange={(e) => setFormData({ ...formData, salaryExpectations: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={step !== 2}
                >
                  <option value="">Select range</option>
                  <option value="30">€30,000 - €40,000</option>
                  <option value="40">€40,000 - €50,000</option>
                  <option value="50">€50,000 - €60,000</option>
                  <option value="60">€60,000 - €70,000</option>
                  <option value="70">€70,000 - €80,000</option>
                  <option value="80">€80,000 - €90,000</option>
                  <option value="90">€90,000 - €100,000</option>
                  <option value="100">€100,000+</option>
                </select>
              </div>

              {step === 2 && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setStep(1)}
                    className="flex-1 border border-gray-300 text-gray-700 font-medium py-2 px-4 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleStep2Next}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Step 3 */}
          <div className={`bg-white rounded-lg shadow-lg p-8 w-80 ${step !== 3 && 'opacity-50'}`}>
            <div className="text-center mb-6">
              <div className="inline-block bg-blue-100 rounded-full px-4 py-1 mb-4">
                <span className="text-blue-600 font-semibold">Step 3</span>
              </div>
              <h2 className="text-xl font-bold text-gray-900">What is your superpower?</h2>
              <p className="text-sm text-gray-600 mt-2">Tell us about your skills</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Technical Skills*</label>
                <p className="text-xs text-gray-500 mb-2">Select programming languages and technologies you know</p>
                <div className="flex flex-wrap gap-2 mb-2 max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2">
                  {formData.skills.length === 0 ? (
                    <span className="text-gray-400 text-sm">No skills selected yet</span>
                  ) : (
                    formData.skills.map((skill, index) => (
                      <span key={index} className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">
                        {skill}
                        {step === 3 && (
                          <button onClick={() => removeSkill(index)} className="hover:text-blue-900">
                            <X size={14} />
                          </button>
                        )}
                      </span>
                    ))
                  )}
                </div>
                {step === 3 && (
                  <div className="relative">
                    <input
                      type="text"
                      value={searchInputs.skill}
                      onChange={(e) => setSearchInputs({ ...searchInputs, skill: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Search for skills (e.g., JavaScript, Python, React)..."
                    />
                    {searchInputs.skill && getFilteredSkills().length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {getFilteredSkills().slice(0, 10).map((skill) => (
                          <button
                            key={skill}
                            onClick={() => addSkill(skill)}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm"
                          >
                            {skill}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Spoken Languages*</label>
                <p className="text-xs text-gray-500 mb-2">Select languages you can speak</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.spokenLanguages.length === 0 ? (
                    <span className="text-gray-400 text-sm">No languages selected yet</span>
                  ) : (
                    formData.spokenLanguages.map((lang, index) => (
                      <span key={index} className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">
                        {lang}
                        {step === 3 && (
                          <button onClick={() => removeSpokenLanguage(index)} className="hover:text-green-900">
                            <X size={14} />
                          </button>
                        )}
                      </span>
                    ))
                  )}
                </div>
                {step === 3 && (
                  <div className="relative">
                    <input
                      type="text"
                      value={searchInputs.language}
                      onChange={(e) => setSearchInputs({ ...searchInputs, language: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Search for languages (e.g., English, German)..."
                    />
                    {searchInputs.language && getFilteredLanguages().length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {getFilteredLanguages().slice(0, 10).map((lang) => (
                          <button
                            key={lang}
                            onClick={() => addSpokenLanguage(lang)}
                            className="w-full text-left px-3 py-2 hover:bg-green-50 text-sm"
                          >
                            {lang}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload CV*</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleFileChange(e, 'cv')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  disabled={step !== 3}
                />
                {formData.cvFile && (
                  <p className="text-xs text-green-600 mt-1">✓ {formData.cvFile.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload Diploma (Optional)</label>
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) => handleFileChange(e, 'diploma')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  disabled={step !== 3}
                />
                {formData.diplomaFile && (
                  <p className="text-xs text-green-600 mt-1">✓ {formData.diplomaFile.name}</p>
                )}
              </div>

              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              {step === 3 && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setStep(2)}
                    className="flex-1 border border-gray-300 text-gray-700 font-medium py-2 px-4 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleCreateAccount}
                    disabled={loading}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Creating...' : 'Create Account'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProspectRegistration;
